import { CalendarCheck, Inbox, KanbanSquare, Menu, Users } from "lucide-react";
import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Modal } from "@/components/Modal";
import type { NavCounts } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * v2.10 P4.2: the phone's navigation (≤ 768 px) — a bottom tab bar, Today · Leads · Boards · Inbox · More.
 * "More" opens the full nav (every group the desktop sidebar has) as a sheet, with the account controls.
 * Tap targets are at least 44 × 44, and the bar sits above the iPhone's home indicator (safe-area inset).
 */
type Tab = { to: string; text: string; icon: ReactNode; match: (path: string) => boolean };

const TABS: Tab[] = [
  { to: "/today", text: "Today", icon: <CalendarCheck className="size-5" aria-hidden />, match: (p) => p === "/today" || p === "/" },
  { to: "/leads", text: "Leads", icon: <Users className="size-5" aria-hidden />, match: (p) => p.startsWith("/leads") },
  {
    to: "/curative/boards",
    text: "Boards",
    icon: <KanbanSquare className="size-5" aria-hidden />,
    match: (p) => p.startsWith("/curative/boards") || p.startsWith("/curative/deals"),
  },
  { to: "/curative/inbox", text: "Inbox", icon: <Inbox className="size-5" aria-hidden />, match: (p) => p.startsWith("/curative/inbox") },
];

export type NavGroup = { heading: string; items: { to: string; label: string; count?: keyof NavCounts }[] };

export function MobileTabBar({ groups, counts, footer }: { groups: NavGroup[]; counts?: NavCounts; footer: ReactNode }) {
  const { pathname } = useLocation();
  const [more, setMore] = useState(false);
  return (
    <>
      <nav
        aria-label="Tabs"
        data-testid="mobile-tabs"
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            data-testid={`tab-${tab.text.toLowerCase()}`}
            className={cn(
              "flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px]",
              tab.match(pathname) ? "font-semibold text-primary" : "text-fg-muted",
            )}
          >
            {tab.icon}
            {tab.text}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMore(true)}
          data-testid="tab-more"
          className="flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] text-fg-muted"
        >
          <Menu className="size-5" aria-hidden />
          More
        </button>
      </nav>
      {more && (
        <Modal title="More" onClose={() => setMore(false)} testId="mobile-more">
          <div className="space-y-4">
            {groups.map((g) => (
              <section key={g.heading}>
                <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">{g.heading}</h3>
                <ul className="divide-y rounded-md border">
                  {g.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        onClick={() => setMore(false)}
                        className="flex min-h-11 items-center justify-between px-3 text-sm"
                      >
                        <span>{item.label}</span>
                        {item.count && counts ? (
                          <span className="text-xs tabular-nums text-fg-muted">{counts[item.count]}</span>
                        ) : null}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <section>
              <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Texting</h3>
              <ul className="divide-y rounded-md border">
                <li>
                  <NavLink to="/sms-templates" onClick={() => setMore(false)} className="flex min-h-11 items-center px-3 text-sm">
                    SMS templates
                  </NavLink>
                </li>
              </ul>
            </section>
            <div className="rounded-md border px-3 py-2">{footer}</div>
          </div>
        </Modal>
      )}
    </>
  );
}
